import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import VyuScanAutoSDK from "./sdk/VyuScanAutoSDKv4";
import Permission from "../components/Permission";
import BrandEngagement from "../components/BrandEngagement";
import SplashScreen from "../components/SplashScreen";
import HowToScan from "../components/HowToScan";
import { CAMERA, PHONE, LOADER_IMAGE_URL } from "../assets";
import { TEXTS } from "./sdk/constants";
import usePosthogEvent from "../hooks/use-posthog-event";
import { v4 as uuid } from "uuid";
import "./styles/VyuScanAuto.scss";
import { webLogger } from '../tracing';
import * as API from "./services/api_services";
import { preloadImage } from "../utils/helperFunctions";
import SupportPopup from "../components/SupportPopup";

const CACHE_KEY = 'user_geolocation_cache';
const CACHE_TIMESTAMP = 'user_geolocation_timestamp';
const CACHE_DURATION = 365 * 24 * 60 * 60 * 1000;

const VyuScanAuto = () => {
  const { productId } = useParams();
  const getShortIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const shortIdParam = urlParams.get("shortId");

    if (shortIdParam) {
      let cleanShortId = decodeURIComponent(shortIdParam).trim();

      if (
        (cleanShortId.startsWith('"') && cleanShortId.endsWith('"')) ||
        (cleanShortId.startsWith("'") && cleanShortId.endsWith("'"))
      ) {
        cleanShortId = cleanShortId.slice(1, -1);
      }

      cleanShortId = cleanShortId.replace(/^["']|["']$/g, "");
      return cleanShortId.trim();
    }

    return null;
  };

  const shortId = getShortIdFromUrl();
  const posthogEvent = usePosthogEvent();

  const [loading, setLoading] = useState(true);
  const [cameraPermission, setCameraPermission] = useState<boolean | null>(null);
  const [locationPermission, setLocationPermission] = useState<boolean | null>(null);
  const [brandEngagementVisible, setBrandEngagementVisible] = useState<boolean>(false);
  const [brandEngagementData, setBrandEngagementData] = useState<any>(null);
  const [vyuScanSDK, setVyuScanSDK] = useState<VyuScanAutoSDK | null>(null);
  const [ipLocation, setIpLocation] = useState<any>(null);
  const [showSupportPopup, setShowSupportPopup] = useState(false);
  const [supportPopupConfig, setSupportPopupConfig] = useState<any>(null);

  // Add splash screen and how-to-scan states
  const [showSplashScreen, setShowSplashScreen] = useState(false);
  const [showHowToScan, setShowHowToScan] = useState(false);
  const [splashScreenDismissed, setSplashScreenDismissed] = useState(false);
  const [howToScanDismissed, setHowToScanDismissed] = useState(false);
  const [productConfig, setProductConfig] = useState<any>(null);
  const [languageConfig, setLanguageConfig] = useState<any>(null);
  const [loaderImageCached, setLoaderImageCached] = useState(false);

  // Add a rerender trigger to force component updates when needed
  const [forceRender, setForceRender] = useState(0);
  // Refs instead of state for tracking initialization
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const initializingRef = useRef<boolean>(false);
  const initializedRef = useRef<boolean>(false);
  const sdkInitializedRef = useRef<boolean>(false);
  const wasHidden = useRef<boolean>(false);
  const visibilityChangeInProgress = useRef<boolean>(false);
  const configLoadInitiated = useRef(false);

  const eventsSent = useRef<Set<string>>(new Set());
  useEffect(() => {
    const cacheLoaderImage = async () => {
      try {
        await preloadImage(LOADER_IMAGE_URL);
        setLoaderImageCached(true);
        console.log("Loader image cached successfully");
      } catch (error) {
        console.warn("Failed to cache loader image:", error);
        setLoaderImageCached(true);
      }
    };

    cacheLoaderImage();
  }, []);
  const sendPostHogEventSafely = useCallback(
    (eventName: string, data: any, uniqueKey?: string) => {
      if (
        !posthogEvent ||
        typeof posthogEvent.sendPostHogEvent !== "function"
      ) {
        console.log(`PostHog not ready yet for: ${eventName}`);
        return;
      }

      const eventKey = uniqueKey || `${eventName}_${JSON.stringify(data)}`;

      if (eventsSent.current.has(eventKey)) {
        return;
      }

      try {
        webLogger.info(eventName, data);
        posthogEvent.sendPostHogEvent(eventName, data);
        eventsSent.current.add(eventKey);
        console.log(`PostHog event sent: ${eventName}`);
      } catch (error) {
        console.error(`Error sending PostHog event ${eventName}:`, error);
      }
    },
    [posthogEvent]
  );

  const getCachedGeolocation = (): Promise<{ lat: number, long: number }> => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      const timestamp = localStorage.getItem(CACHE_TIMESTAMP);

      if (cached && timestamp) {
        const age = Date.now() - parseInt(timestamp);
        if (age < CACHE_DURATION) {
          const cachedCoords = JSON.parse(cached);
          console.log('Coordinates loaded from cache:', cachedCoords);
          return Promise.resolve(cachedCoords);
        }
      }
    } catch (error) {
      console.warn('Error checking geolocation cache:', error);
    }

    return new Promise((resolve, reject) => {
      console.log('Fetching fresh coordinates...');

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            long: position.coords.longitude
          };

          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(coords));
            localStorage.setItem(CACHE_TIMESTAMP, Date.now().toString());
            console.log('Fresh coordinates fetched and cached:', coords);
          } catch (error) {
            console.warn('Error saving coordinates to cache:', error);
          }

          resolve(coords);
        },
        (error) => {
          console.error('Geolocation error:', error.message, 'Code:', error.code);

          if (error.code === error.PERMISSION_DENIED) {
            console.log('User denied geolocation permission');
            reject(error);
            return;
          }

          try {
            const cachedData = localStorage.getItem(CACHE_KEY);
            if (cachedData) {
              const cachedCoords = JSON.parse(cachedData);
              console.log('Using expired cache as fallback:', cachedCoords);
              resolve(cachedCoords);
              return;
            }
          } catch (cacheError) {
            console.warn('Error retrieving expired cache:', cacheError);
          }

          reject(error);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 5 * 60 * 1000,
        }
      );
    });
  };

  const handleGeolocationWithCaching = async () => {
    if (navigator.geolocation) {
      try {
        const coords = await getCachedGeolocation();
        setIpLocation(prevLocation => ({
          ...prevLocation,
          latitude: coords.lat,
          longitude: coords.long,
        }));
        console.log('Coordinates set:', coords);
        posthogEvent.sendPostHogEvent('locationPermissionGranted', {
          latitude: coords.lat,
          longitude: coords.long,
        });
        return coords;
      } catch (error) {
        console.error('Error fetching coordinates:', error.message);

        posthogEvent.sendPostHogEvent('location_permission_failed', {
          error: error.message,
        });

        console.log('Using IP location fallback:', ipLocation);
        throw error;
      }
    } else {
      console.warn('Geolocation not supported by this browser.');

      posthogEvent.sendPostHogEvent('location_permission_failed', {
        error: 'geolocation_not_supported',
        source: 'browser_unsupported',
      });

      throw new Error('Geolocation not supported by this browser.');
    }
  };

  // Add config loading functions for VyuScanAuto
  const loadProductConfig = async () => {
    if (configLoadInitiated.current) {
      console.log('Config already loading/loaded, skipping...');
      return;
    }
    setLoading(true)
    configLoadInitiated.current = true;

    try {
      if (!productId) {
        throw new Error("Product ID not provided");
      }

      console.log('Loading product config for productId:', productId);
      const configResponse = await API.fetchProductConfig(productId);

      if (!configResponse || !configResponse.configs) {
        throw new Error("Invalid config response");
      }

      console.log('Product config loaded:', configResponse);
      setProductConfig(configResponse);
      
      // Set basic language config (you can expand this as needed)
      const langConfig = {
        cameraAccessNeeded: 'Camera access needed',
        allowCameraAccess: 'Allow access to the camera to scan the product',
        enableCamera: 'Enable Camera',
        locationAccessNeeded: 'Location access needed',
        locationPurpose: "To help identify & report fake products. \n Don't worry, this data is safe & anonymized.",
        enableLocation: 'Enable Location',
        startScanning: 'Start Scanning',
        howToScan: 'How to scan?',
        howToScanDescription: 'Position the QR code within the frame and tap to scan',
      };
      setLanguageConfig(langConfig);

      posthogEvent.sendPostHogEvent('product_config_fetched', {
        qr_id: shortId || "unknown",
        productId: productId,
      });

      return configResponse;
    } catch (error) {
      webLogger.error('Error loading product config', error);
      posthogEvent.sendPostHogEvent('product_config_failed', {
        qr_id: shortId || "unknown",
        productId: productId,
        error: error.message,
      });
      console.error('Error loading product config:', error);
      setProductConfig(null);
      throw error;
   } finally {
      setLoading(false)
    }
  };

  // Create mapped config for components
  const createMappedConfigs = (productConfig: any) => {
    if (!productConfig?.configs?.experience_config?.brand_engagement) {
      return { splashConfig: null, howToScanConfig: null };
    }

    const brandEngagement = productConfig.configs.experience_config.brand_engagement;
    const customSplashScreenConfig = brandEngagement.customSplashScreenConfig;
    const placeholderContent = brandEngagement.placeholder_question_mark_content;
    const howToScanConfigCustom = brandEngagement?.howToScanConfigCustom
    // Map splash screen config to match original layout
    const splashConfig = customSplashScreenConfig ? {
      splashScreenConfig: {
        brandLogo: customSplashScreenConfig.logo,
        brandName: customSplashScreenConfig.brandName || '',
        appTitle: customSplashScreenConfig.text || 'AUTHENTICITY CHECKER',
        startButtonText:  customSplashScreenConfig.startButtonText || 'GET STARTED',
        showLanguageButton: false,
        layout: {
          logo: customSplashScreenConfig?.styles?.logo ?? {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            marginTop: '5%',
            height: '20%',
            marginBottom: '5%',
            position: 'absolute',
            top: '0'
          },
          logoImage: customSplashScreenConfig?.styles?.logoImage ?? {
            objectFit: 'contain',
            maxHeight: customSplashScreenConfig.text ? '75%' : '100%',
            maxWidth: '100%'
          },
          appTitle: customSplashScreenConfig?.styles?.appTitle ?? {
            color: '#283583',
            //fontFamily: 'sans-serif',
            fontWeight: '700',
            fontSize: '18px',
            height: '20%',
            textAlign: 'center',
            position: 'absolute',
            top: '20%',
            marginTop: '10px'
          },
          buttons: {
            startButton: customSplashScreenConfig?.styles?.startButton ?? {
              backgroundColor: 'rgba(235, 111, 49, 1)',
              color: 'rgba(255, 255, 255, 1)',
            }
          }
        },
        backgroundStyles: customSplashScreenConfig?.styles?.backgroundStyles ?? {
    background: customSplashScreenConfig.backgroundImage ? 
            `url(${customSplashScreenConfig.backgroundImage}) center/cover` : 
            'white',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          //backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundColor: 'white',
          backgroundSize: '70%'
        }
      },
      featureFlags: {
        showSplashScreen: customSplashScreenConfig.showStartButton === "true" || true
      }
    } : null;

    // Map how-to-scan config
    const howToScanConfig = placeholderContent || howToScanConfigCustom?.url ?  {
      howToScanConfig: {
        howToScanTitle: howToScanConfigCustom?.title || 'How to scan?',
        howToScanVideoUrl: howToScanConfigCustom?.url || placeholderContent,
        howToScanDescription: howToScanConfigCustom?.description || 'Position the QR code within the frame and tap to scan',
        startButtonText: howToScanConfigCustom?.startButtonText || 'Start Scanning',
        backgroundColor: howToScanConfigCustom?.backgroundColor || '#ffffff',
        primaryColor: howToScanConfigCustom?.primaryColor || '#333333',
        secondaryColor: howToScanConfigCustom?.secondaryColor || '#FFFFFF',
        buttonDisableTimeout: howToScanConfigCustom?.buttonDisableTimeout || brandEngagement.buttonDisableTimeout || 5000,
        startButton: howToScanConfigCustom?.startButtonStyles ?? {
          backgroundColor: '#343739',
          color: '#ffffff',
          padding: '12px 20px',
          borderRadius: '8px',
          fontSize: '16px',
          border: 'none',
          fontWeight: '600',
          cursor: 'pointer',
          //width: '100%'
        }
      },
      featureFlags: {
        showHowToScan: true
      }
    } : null;

    return { splashConfig, howToScanConfig };
  };

  useEffect(() => {
    if (posthogEvent) {
      sendPostHogEventSafely(
        "url_redirect",
        {
          qr_id: shortId || "unknown",
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          referring_url: document.referrer || "direct",
        },
        "url_redirect"
      );
    }
  }, [posthogEvent, shortId, sendPostHogEventSafely]);

  // Define stable callback functions at the component level
  const toggleBrandEngagementCallback = useCallback((data = {}) => {
    setBrandEngagementData(data);
    setBrandEngagementVisible((prev) => !prev);
  }, []);

  const handleSuccess = useCallback((data) => {
    //console.log("Success callback from SDK:", data);
  }, []);

  const handleError = useCallback((error) => {
    console.error("Error callback from SDK:", error);
  }, []);
  
  const showSupportNowPopup = (data: any) => {
  let supportConfig = {
    title: "Support",
    primaryColor: "linear-gradient(59deg, #2624b6 16.56%, #8c00b6 88.31%);",
    tabs: {
      bestPractices: {
        tabLabel: "Best Practices",
        content: [
          "Check internet speed **•500** kbps required",
          "Check lighting **•Better lighting** = quicker results",
          "Hold phone still **•Stable hands** = quicker results",
        ],
        contentTitle: "Are the scans taking too long?",
        buttonText: "Try Again",
      },
      report: {
        tabLabel: "Report",
        placeholder:
          "Describe your issues - add details like lighting conditions & internet speed",
        buttonText: "Report Issue",
        contentTitle: "Unhappy with the results?",
      },
    },
    autoCloseDelay: 5,
  };
  
  const customSupportConfig = productConfig?.configs?.experience_config?.brand_engagement?.supportConfig;
  supportConfig = customSupportConfig ?? supportConfig;
  setSupportPopupConfig({...data, supportConfig});
  setShowSupportPopup(true);
  };

  const handleCloseSupportPopup = () => {
    setShowSupportPopup(false);
  };

  const handleSupportScanAgain = () => {
    setShowSupportPopup(false);
    if (vyuScanSDK) {
      vyuScanSDK.handleScanAgain();
    }
  };

  const fetchIpAndLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/')
      const data = await response.json()
      setIpLocation({
        city: data.city,
        region: data.region,
        country: data.country_name,
        latitude: data.latitude,
        longitude: data.longitude,
      })
      posthogEvent.sendPostHogEvent('ip_and_location_captured', {
        ip: data.ip,
        city: data.city,
        region: data.region,
        country: data.country_name,
        latitude: data.latitude,
        longitude: data.longitude,
      })
    } catch (error) {
       setIpLocation({
        city: '',
        region: '',
        country: '',
        latitude: '',
        longitude: '',
      })
      console.error('Error fetching IP and location:', error)
      posthogEvent.sendPostHogEvent('ip_and_location_failed', {
        error: error.message,
      })
    }
  }

  // Add splash screen and how-to-scan handlers
  const handleSplashScreenStart = () => {
    console.log('Starting scanning from splash screen');
    setShowSplashScreen(false);
    setSplashScreenDismissed(true);

    const showHowToScanFlag = howToScanConfig?.featureFlags?.showHowToScan;
    if (showHowToScanFlag) {
      setShowHowToScan(true);
    }

    posthogEvent.sendPostHogEvent('splash_screen_start_clicked', {
      qr_id: shortId || "unknown",
    });
  };

  const handleHowToScanStart = () => {
    console.log('Starting scanning from how to scan screen');
    setShowHowToScan(false);
    setHowToScanDismissed(true);

    posthogEvent.sendPostHogEvent('how_to_scan_start_clicked', {
      qr_id: shortId || "unknown",
    });
  };

  useEffect(() => {
    const initializeApp = async () => {
      // Reset all states and refs on page load
      //configLoadInitiated.current = false;
      setSplashScreenDismissed(false);
      setHowToScanDismissed(false);
      setShowSplashScreen(false);
      setShowHowToScan(false);

      // Load product config
      try {
        await loadProductConfig();
      } catch (error) {
        webLogger.error('Error loading product config', error);
        console.error('Product config loading failed:', error);
        // Show error page or redirect
        window.location.href = "/error/invalid";
        return;
      }

      // Generate or retrieve app session ID
      let appSessionId = sessionStorage.getItem("appSessionId");
      if (!appSessionId) {
        appSessionId = uuid();
        sessionStorage.setItem("appSessionId", appSessionId);
      }

      // Check permissions with improved detection
      const checkPermissions = async () => {
        try {
          localStorage.setItem("RetryVideoLoadError", "");
          console.log("Checking permissions...");

          const cameraStatus = await navigator.permissions.query({
            name: "camera" as PermissionName,
          });
          const cameraGranted = cameraStatus.state === "granted";
          setCameraPermission(cameraGranted);

          const locationStatus = await navigator.permissions.query({ name: 'geolocation' })
          const locationGranted = locationStatus.state === 'granted'
          setLocationPermission(locationGranted)
          if(locationGranted === false){
            try {
              localStorage.removeItem(CACHE_KEY);
              localStorage.removeItem(CACHE_TIMESTAMP);
              console.log('Cache keys cleared successfully');
            } catch (error) {
              console.error('Error clearing cache keys:', error);
            }
          }
        } catch (err) {
          console.error("Error checking permissions:", err);
        } finally {
          //setLoading(false);
        }
      };

      checkPermissions();
    };

    initializeApp();
  }, []);

  useEffect(() => {
     fetchIpAndLocation()
  }, []);

  // Check if all prerequisite screens are dismissed
  const allScreensDismissed = () => {
    if (!productConfig) return false;
    
    const { splashConfig, howToScanConfig } = createMappedConfigs(productConfig);
    
    const showSplashScreenFlag = splashConfig?.featureFlags?.showSplashScreen;
    const showHowToScanFlag = howToScanConfig?.featureFlags?.showHowToScan;

    if (showSplashScreenFlag && !splashScreenDismissed) {
      return false;
    }

    if (showHowToScanFlag && !howToScanDismissed) {
      return false;
    }

    return true;
  };

  // Show splash screen if needed
  useEffect(() => {
    if (productConfig && !loading) {
      const { splashConfig } = createMappedConfigs(productConfig);
      const showSplashScreenFlag = splashConfig?.featureFlags?.showSplashScreen;
      
      if (showSplashScreenFlag && !splashScreenDismissed) {
        setShowSplashScreen(true);
      } else if (!showSplashScreenFlag && !splashScreenDismissed) {
        setSplashScreenDismissed(true);
        
        const { howToScanConfig } = createMappedConfigs(productConfig);
        const showHowToScanFlag = howToScanConfig?.featureFlags?.showHowToScan;
        if (showHowToScanFlag && !howToScanDismissed) {
          setShowHowToScan(true);
        }
      }
    }
  }, [productConfig, loading, splashScreenDismissed]);

  // Show how-to-scan if needed
  useEffect(() => {
    if (productConfig && !loading && splashScreenDismissed) {
      const { howToScanConfig } = createMappedConfigs(productConfig);
      const showHowToScanFlag = howToScanConfig?.featureFlags?.showHowToScan;
      
      if (showHowToScanFlag && !howToScanDismissed) {
        setShowHowToScan(true);
      }
    }
  }, [productConfig, loading, splashScreenDismissed, howToScanDismissed]);

  // Initialize SDK when permissions are granted and screens are dismissed
  useEffect(() => {
    if (initializingRef.current || sdkInitializedRef.current) {
      return;
    }

    const createAndInitSDK = async () => {
      if (
        !cameraPermission ||
        !locationPermission ||
        !videoRef.current ||
        !productId ||
        !allScreensDismissed()
      ) {
        console.log("Skipping SDK initialization: required conditions not met");
        return;
      }

      if (sdkInitializedRef.current) {
        console.log("SDK already initialized, skipping");
        return;
      }

      if (initializingRef.current) {
        console.log("SDK initialization already in progress, skipping");
        return;
      }

      initializingRef.current = true;

      try {
        if (videoRef.current) {
          videoRef.current.style.display = "block";
          videoRef.current.style.backgroundColor = "#000";
        }

        const sdk = new VyuScanAutoSDK(videoRef.current);

        // Pass the productConfig to SDK
        await sdk.init(
          toggleBrandEngagementCallback,
          posthogEvent,
          handleSuccess,
          handleError,
          ipLocation,
          productConfig,
          showSupportNowPopup
        );

        console.log("SDK initialized successfully");
        setVyuScanSDK(sdk);

        sdkInitializedRef.current = true;

        if (typeof window !== "undefined") {
          (window as any)._vyuScanSDK = sdk;
        }
        console.log("No tutorial needed, explicitly running SDK");
        setTimeout(() => {
          console.log("Explicit call to sdk.run()");
          sdk.run();
        }, 100);

        console.log("✅ SDK initialization completed successfully");
      } catch (error) {
        console.error("❌ Error initializing SDK:", error);
        sdkInitializedRef.current = false;
      } finally {
        initializingRef.current = false;
      }
    };

    if (cameraPermission && locationPermission && videoRef.current && allScreensDismissed()) {
      setTimeout(() => {
        createAndInitSDK();
      }, 300);
    }
  }, [
    cameraPermission,
    locationPermission,
    productId,
    posthogEvent,
    toggleBrandEngagementCallback,
    handleSuccess,
    handleError,
    ipLocation,
    splashScreenDismissed,
    howToScanDismissed,
    productConfig
  ]);

  // Handle document visibility for camera restart
  useEffect(() => {
    if (!vyuScanSDK) return;

    const handleVisibilityChange = async () => {
      if (visibilityChangeInProgress.current) {
        return;
      }

      if (document.hidden) {
        wasHidden.current = true;

        if (vyuScanSDK) {
          vyuScanSDK.cleanup(true);
        }
      } else {
        if (
          wasHidden.current &&
          vyuScanSDK &&
          !brandEngagementVisible &&
          vyuScanSDK.state?.loaded &&
          !vyuScanSDK.state.resultsShown &&
          !vyuScanSDK.state.verifying &&
          !vyuScanSDK.state.uploadingImages
        ) {
          wasHidden.current = false;
          visibilityChangeInProgress.current = true;

          try {
            const timeoutPromise = new Promise<void>((_, reject) => {
              setTimeout(
                () => reject(new Error("Camera restart timed out")),
                10000
              );
            });

            try {
              await Promise.race([vyuScanSDK.restartCamera(), timeoutPromise]);
              console.log("Camera restart completed successfully");
            } catch (timeoutError) {
              console.error(
                "Camera restart timed out or failed:",
                timeoutError
              );
              await vyuScanSDK.startViewfinder();
            }
          } catch (error) {
            console.error("Error resuming SDK after visibility change:", error);

            setTimeout(() => {
              console.log("Forcing component reset as last resort");
              setForceRender((prev) => prev + 1);
            }, 500);
          } finally {
            visibilityChangeInProgress.current = false;
          }
        } else {
          console.log(
            "Tab became visible but conditions not met for camera restart:",
            {
              wasHidden: wasHidden.current,
              brandEngagementVisible,
              sdkExists: !!vyuScanSDK,
              sdkLoaded: vyuScanSDK?.state?.loaded,
              resultsShown: vyuScanSDK?.state?.resultsShown,
              verifying: vyuScanSDK?.state?.verifying,
              uploadingImages: vyuScanSDK?.state?.uploadingImages,
            }
          );
          wasHidden.current = false;
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    vyuScanSDK,
    brandEngagementVisible,
    posthogEvent,
    toggleBrandEngagementCallback,
    handleSuccess,
    handleError,
  ]);

  const requestCameraPermission = async () => {
    try {
      const isDesktop = !/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
      await navigator.mediaDevices.getUserMedia({
        video: { facingMode: isDesktop ? { ideal: 'user' } : { ideal: 'environment' } },
      })
      posthogEvent.sendPostHogEvent('cameraPermissionGranted', {
      })
      setCameraPermission(true)
    } catch (err) {
      posthogEvent.sendPostHogEvent('cameraPermissionGranted', {
        error: err,
      })
      console.error('Camera permission denied:', err)
      setCameraPermission(false)
      alert('Camera permission is denied. Please enable camera access in your browser settings.')
    }
  }

  const requestLocationPermission = async () => {
    try {
      if (!navigator.geolocation) {
        console.warn('Geolocation not supported by this browser.');
        posthogEvent.sendPostHogEvent('location_permission_failed', {
          error: 'geolocation_not_supported',
          source: 'browser_unsupported',
        });
        throw new Error('Geolocation not supported by this browser.');
      }

      const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
      console.log('Current geolocation permission status:', permissionStatus.state);

      if (permissionStatus.state === 'denied') {
        console.log('Geolocation permission is denied');
        posthogEvent.sendPostHogEvent('location_permission_failed', {
          error: 'permission_denied',
          source: 'user_denied',
        });
        alert('Location permission is denied. Please enable location access in your browser settings.');
        return;
      }

      await handleGeolocationWithCaching();
      setLocationPermission(true);
      
    } catch (error) {
      console.log('Location permission request failed:', error.message);
      
      if (error.code === error.PERMISSION_DENIED) {
        posthogEvent.sendPostHogEvent('location_permission_failed', {
          error: 'permission_denied_by_user',
          source: 'user_interaction',
        });
        alert('Location permission was denied. Please enable location access to continue.');
      } else {
        // console.log('Using IP location fallback due to error:', error.message);
        posthogEvent.sendPostHogEvent('location_permission_failed', {
          error: error.message,
          source: 'geolocation_error',
        });
        
        setLocationPermission(false);
      }
    }
  };



  // Get mapped configs for components
  const { splashConfig, howToScanConfig } = productConfig ? createMappedConfigs(productConfig) : { splashConfig: null, howToScanConfig: null };

    // Determine what to show
  const shouldShowSplashScreen = productConfig && !splashScreenDismissed && splashConfig?.featureFlags?.showSplashScreen;
  
  const shouldShowHowToScan = productConfig && !howToScanDismissed && splashScreenDismissed && howToScanConfig?.featureFlags?.showHowToScan;
  const shouldShowSupportPopup = supportPopupConfig && showSupportPopup;
  // Force render key for component
  const componentKey = `vyu-scan-${cameraPermission}-${locationPermission}-${forceRender}`;

  return (
    <div
      className="VyuScanAutoWrapper"
      onContextMenu={(e) => e.preventDefault()}
      key={componentKey}
    >
      {shouldShowSplashScreen && (
        <SplashScreen
          appConfig={null}
          clientConfig={splashConfig}
          onStartScanning={handleSplashScreenStart}
          onChooseLanguage={null}
          languageConfig={languageConfig}
        />
      )}

      {shouldShowHowToScan && (
        <HowToScan
          appConfig={null}
          clientConfig={howToScanConfig}
          onStartScanning={handleHowToScanStart}
          languageConfig={languageConfig}
          buttonDisableTimeout={howToScanConfig?.howToScanConfig?.buttonDisableTimeout}
        />
      )}

      {loading && !shouldShowSplashScreen && !shouldShowHowToScan ? (
        // <div className="loader-wrapper">
        //   <div className="loader"></div>
        // </div>
                <div className="VyuScanWrapper__LoadingContainer">
          <img
            src={LOADER_IMAGE_URL}
            alt="Loading..."
            style={{
              opacity: loaderImageCached ? 1 : 0.8,
              transition: "opacity 0.3s ease-in-out",
              maxWidth: 200,
              maxHeight: 200,
              height: "auto",
            }}
            onLoad={() => {
              console.log("Loader image displayed successfully");
            }}
            onError={(e) => {
              console.error("Failed to load loader image");
              e.target.style.display = "none";
            }}
          />
        </div>
      ) : (
        <>
          {!loading && !shouldShowSplashScreen && !shouldShowHowToScan && (
            <>
              {!cameraPermission && (
                <Permission
                  name={languageConfig?.cameraAccessNeeded || TEXTS.CAMERA_ACCESS}
                  text={languageConfig?.allowCameraAccess || TEXTS.ALLOW_CAMERA}
                  image={PHONE}
                  cta={languageConfig?.enableCamera || TEXTS.ENABLE_CAMERA}
                  onClick={requestCameraPermission}
                  posthogEvent={posthogEvent}
                />
              )}

              {cameraPermission && !locationPermission && (
                <Permission
                  name={languageConfig?.locationAccessNeeded || TEXTS.LOCATION_ACCESS}
                  text={languageConfig?.locationPurpose || TEXTS.ALLOW_LOCATION}
                  image={CAMERA}
                  cta={languageConfig?.enableLocation || TEXTS.ENABLE_LOCATION}
                  onClick={requestLocationPermission}
                  posthogEvent={posthogEvent}
                />
              )}

              {cameraPermission && locationPermission && (
                <div
                  id="vyu-wrapper"
                  className="VyuWrapper"
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "relative",
                    overflow: "hidden",
                    //backgroundColor: "#000",
                  }}
                >
                  <video
                    ref={videoRef}
                    id="vyu-stream"
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: "100vw",
                      height: "100vh",
                      objectFit: "cover",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      zIndex: 1,
                      //backgroundColor: "#000",
                      display: "block",
                    }}
                  />
                </div>
              )}

              {cameraPermission && locationPermission && brandEngagementVisible && (
                <BrandEngagement
                  brandEngagementData={brandEngagementData}
                  setBrandEngagementVisible={setBrandEngagementVisible}
                />
              )}
            </>
          )}

        </>
      )}
      {shouldShowSupportPopup && (
      <SupportPopup
        isVisible={shouldShowSupportPopup}
        onClose={handleCloseSupportPopup}
        onScanAgain={handleSupportScanAgain}
        config={supportPopupConfig?.supportConfig}
        languageConfig={languageConfig}
        sessionId={supportPopupConfig?.sessionId}
        productInfo={supportPopupConfig?.productInfo}
        posthogEvent={posthogEvent}
      />
    )}
    </div>
  );
};

export default VyuScanAuto;